import {Actions} from 'constants/permissions/actions';
import {TeamRoles} from 'constants/roles';
import {ReactNode} from 'react';
import {useSafeCurrentCompanyUuid} from 'ui/@atoms/current-company';
import {useCompanyUserProfile} from 'ui/@hooks/queries/use-company-user-profile';
import {useFullUserProfile} from 'ui/@hooks/queries/use-full-user-profile';
import {useUserPermission} from 'ui/@hooks/use-user-permission';

interface HasPermissionProps {
  action: Actions;
  children: ReactNode;
  givenUserProfileUuid?: string;
}

export const useHasPermission = (givenUserProfileUuid?: string) => {
  const companyUuid = useSafeCurrentCompanyUuid();

  const userProfile = useFullUserProfile({
    variables: {
      companyUuid,
    },
  });

  const companyUserProfile = useCompanyUserProfile({
    variables: {
      companyUuid,
      userProfileUuid: givenUserProfileUuid,
    },
    enabled: !!givenUserProfileUuid,
  });

  const teamsRoles = companyUserProfile.data?.team?.teamManagers
    .map(({uuid}) => uuid)
    .includes(userProfile.data?.uuid || '')
    ? [TeamRoles.MANAGER]
    : [];

  if (
    companyUserProfile.data?.uuid &&
    companyUserProfile.data?.uuid === userProfile.data?.uuid
  ) {
    teamsRoles.push(TeamRoles.CURRENT_USER);
  }

  const hasPermission = useUserPermission(
    userProfile.data?.role || '',
    teamsRoles
  );

  return hasPermission;
};

const HasPermission = ({
  action,
  children,
  givenUserProfileUuid,
}: HasPermissionProps) => {
  const hasPermission = useHasPermission(givenUserProfileUuid);

  if (!hasPermission(action)) return <></>;

  return <>{children}</>;
};

interface HasAnyPermissionProps {
  actions: Actions[];
  children: ReactNode;
}

export const HasAnyPermission = ({
  actions,
  children,
}: HasAnyPermissionProps) => {
  const hasPermission = useHasPermission();

  if (actions.some((action) => hasPermission(action))) return <>{children}</>;

  return <></>;
};

interface HasAllPermissionProps {
  actions: Actions[];
  children: ReactNode;
}

export const HasAllPermission = ({
  actions,
  children,
}: HasAllPermissionProps) => {
  const hasPermission = useHasPermission();

  if (actions.every((action) => hasPermission(action))) return <>{children}</>;

  return <></>;
};

export default HasPermission;
